import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { formatVND, formatCompact } from '@/lib/utils';
import { addPayment, correctDebt } from '../../../services/debtService';
import { addLoan } from '../../../ledger/ledger';
import { saveTemplate } from '../../../ledger/plans';
import { updateLoanTerms } from '../../../ledger/maintenance';
import { amortizationSchedule, fixedMonthlyPayment, splitDebtPayment, validateLoanCorrection, hasInterestRate } from '../../../finance';
import { useData } from '../../store/data';
import { FormSheet, Field, MoneyField, WalletSelect } from '../../components/FormSheet';
import { parseAmount, parseMoneyOrNull } from '../entry/formSchema';
import { useAssetsUI, useAction, uid } from './useAssetsUI';
import { toDate } from '../transactions/entries';

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const dmy = (d: Date | null) => (d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` : '—');

// ───────── Thêm khoản vay ─────────
export function AddLoanSheet({ onSaved }: { onSaved: () => void }) {
  const close = useAssetsUI(s => s.close);
  const wallets = useData(s => s.data.sources).filter(s => s.status !== 'inactive' && s.type !== 'credit');
  const [lender, setLender] = useState(''); const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState(''); const [term, setTerm] = useState('');
  const [method, setMethod] = useState<'annuity' | 'flexible'>('flexible');
  const [monthly, setMonthly] = useState(''); const [due, setDue] = useState('');
  const [wallet, setWallet] = useState(''); const [received, setReceived] = useState('');
  const cards = useData(s => s.data.sources).filter(s => s.type === 'credit' && s.status !== 'inactive');
  const [card, setCard] = useState('none');
  const nextMonth = () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
  const [makeTpl, setMakeTpl] = useState(false); const [payDay, setPayDay] = useState('15');
  const [firstMonth, setFirstMonth] = useState(nextMonth()); const [payWallet, setPayWallet] = useState('');
  const [err, setErr] = useState<Record<string, string>>({});
  const { busy, error, exec } = useAction(() => { close(); onSaved(); });

  const p = parseAmount(principal);
  const suggested = method === 'annuity' && p > 0 && Number(term) > 0 ? Math.round(fixedMonthlyPayment(p, Number(rate) || 0, Number(term))) : 0;

  const perMonth = parseAmount(monthly) || suggested;
  const preview = method === 'annuity' && p > 0 && perMonth > 0 ? amortizationSchedule(p, Number(rate) || 0, perMonth) : [];
  const previewInterest = preview.reduce((s, r) => s + r.interestPaid, 0);

  const submit = () => {
    const e: Record<string, string> = {};
    if (makeTpl) {
      if (!(perMonth > 0)) e.monthly = 'Cần số trả mỗi tháng để tạo mẫu định kỳ';
      if (!payWallet) e.payWallet = 'Chọn ví dùng để trả nợ hằng tháng';
      if (!(Number(payDay) >= 1 && Number(payDay) <= 31)) e.payDay = 'Ngày từ 1 đến 31';
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(firstMonth)) e.firstMonth = 'Chọn tháng trả đầu tiên';
    }
    if (!lender.trim()) e.lender = 'Nhập tên bên cho vay';
    if (p <= 0) e.principal = 'Nhập số tiền vay lớn hơn 0';
    if (!rate.trim()) e.rate = 'Nhập lãi suất; vay không lãi thì nhập 0';
    else if (!(Number(rate) >= 0)) e.rate = 'Lãi suất không hợp lệ';
    if (method === 'annuity' && !(Number(term) > 0)) e.term = 'Trả góp đều cần số tháng';
    if (wallet && received.trim() && parseAmount(received) <= 0) e.received = 'Số tiền thực nhận không hợp lệ';
    setErr(e);
    if (Object.keys(e).length) return;
    exec(async () => {
      const r = await addLoan(uid(), {
        lender, principal: p, interestRate: Number(rate) || 0, term: Number(term) || 0,
        dueDate: due || null, repaymentMethod: method,
        monthlyPayment: perMonth || null,
        receiveWalletId: card === 'none' ? wallet || undefined : undefined, actualReceived: card === 'none' && received.trim() ? parseAmount(received) : undefined,
        creditCardId: card === 'none' ? undefined : card
      });
      if (!r.success || !makeTpl) return r;
      const w = wallets.find(x => x.id === payWallet);
      const t = await saveTemplate(uid(), null, {
        type: 'payment', paymentType: 'debt', debtId: String(r.id), debtName: lender.trim(), name: `Trả nợ ${lender.trim()}`,
        amount: perMonth, category: '', walletId: payWallet, walletName: w?.name, frequency: 'monthly', dayOfMonth: Number(payDay), active: true, startMonth: firstMonth
      });
      return t.success ? r : { ...r, warnings: [...(r.warnings ?? []), `Đã thêm khoản vay nhưng chưa tạo được mẫu trả nợ định kỳ: ${t.error ?? ''} Thêm lại ở Dự kiến › Mẫu định kỳ.`] };
    }, makeTpl ? 'Đã thêm khoản vay và mẫu trả nợ hằng tháng' : 'Đã thêm khoản vay');
  };

  return (
    <FormSheet open onClose={close} title="Thêm khoản vay" onSubmit={submit} submitLabel="Thêm khoản vay" busy={busy} error={error}
      description="Chọn ví nhận tiền để tiền vay vào ví ngay — được ghi là NỢ TĂNG, không phải thu nhập.">
      <Field label="Bên cho vay" error={err.lender}><Input value={lender} onChange={e => setLender(e.target.value)} placeholder="vd: Shinhan Bank" aria-invalid={!!err.lender} /></Field>
      <MoneyField label="Số tiền vay (gốc)" value={principal} onChange={setPrincipal} error={err.principal} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Lãi suất (%/năm)" error={err.rate} hint="Nhập 0 nếu vay không lãi"><Input value={rate} onChange={e => setRate(e.target.value)} inputMode="decimal" placeholder="vd: 9.5" aria-invalid={!!err.rate} /></Field>
        <Field label="Kỳ hạn (tháng)" error={err.term}><Input value={term} onChange={e => setTerm(e.target.value)} inputMode="numeric" placeholder="vd: 36" /></Field>
      </div>
      <Field label="Cách trả">
        <Select value={method} onValueChange={v => setMethod(v as any)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="flexible">Linh hoạt (trả khi có tiền)</SelectItem>
            <SelectItem value="annuity">Trả góp đều hằng tháng (gốc + lãi)</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <MoneyField label="Trả mỗi tháng (tuỳ chọn)" value={monthly} onChange={setMonthly} allowZero error={err.monthly} hint={suggested > 0 ? `Gợi ý theo kỳ hạn: ${formatVND(suggested)}/tháng` : undefined} />
      {preview.length > 0 && (
        <p className="num rounded-md bg-muted px-3 py-2 text-sm">
          Trả <b>{formatVND(perMonth)}</b>/tháng → hết nợ sau <b>{preview.length} kỳ</b>, tổng lãi <b>{formatCompact(previewInterest)}</b>.
          Kỳ 1: lãi {formatVND(Math.round(preview[0].interestPaid))}, gốc {formatVND(Math.round(preview[0].principalPaid))}; kỳ cuối: lãi {formatVND(Math.round(preview[preview.length - 1].interestPaid))}.
        </p>
      )}
      <Field label="Hạn trả cuối (tuỳ chọn)"><Input type="date" value={due} onChange={e => setDue(e.target.value)} className="num" /></Field>
      {cards.length > 0 && (
        <Field label="Vay hoặc trả góp trên thẻ tín dụng (tuỳ chọn)" hint="Chọn thẻ nếu khoản này nằm trong dư nợ của một thẻ. Số tiền được tính một lần trong dư nợ thẻ, không cộng thêm vào tổng nợ.">
          <Select value={card} onValueChange={setCard}>
            <SelectTrigger aria-label="Thẻ tín dụng của khoản vay"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Không, đây là khoản vay riêng</SelectItem>{cards.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      {card === 'none' && <WalletSelect label="Ví nhận tiền vay (tuỳ chọn)" value={wallet} onChange={setWallet} wallets={wallets} placeholder="Không nhận vào ví nào" />}
      <label className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={makeTpl} onChange={e => setMakeTpl(e.target.checked)} />
        <span>Tạo khoản trả nợ <b>hằng tháng</b> ở mục Dự kiến — mỗi kỳ chỉ cần xác nhận, lãi và gốc được tách tự động.</span>
      </label>
      {makeTpl && (
        <div className="space-y-3">
          <WalletSelect label="Trả nợ từ ví" value={payWallet} onChange={setPayWallet} wallets={wallets} error={err.payWallet} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ngày trả mỗi tháng" error={err.payDay}><Input value={payDay} onChange={e => setPayDay(e.target.value)} inputMode="numeric" /></Field>
            <Field label="Tháng trả đầu tiên" error={err.firstMonth}><Input type="month" value={firstMonth} onChange={e => setFirstMonth(e.target.value)} className="num" /></Field>
          </div>
        </div>
      )}
      {wallet && <MoneyField label="Số tiền thực nhận" value={received} onChange={setReceived} error={err.received} hint="Để trống = bằng số vay. Nhập thấp hơn nếu bị trừ phí." />}
    </FormSheet>
  );
}

// ───────── Trả nợ ─────────
export function PayDebtSheet({ id, onSaved }: { id: string; onSaved: () => void }) {
  const close = useAssetsUI(s => s.close);
  const debt = useData(s => s.data.debts).find(d => d.id === id);
  const wallets = useData(s => s.data.sources).filter(s => s.status !== 'inactive' && s.type !== 'credit');
  const remaining = Number(debt?.remainingBalance) || 0;
  const monthlyInterest = Math.round(remaining * ((Number(debt?.interestRate) || 0) / 12 / 100));
  const fixed = Number(debt?.monthlyPayment) || 0;
  // Nhập đúng như thông báo ngân hàng: tiền lãi + tiền gốc; tổng tự cộng. Khoản có mức trả cố định gợi ý sẵn theo bảng niên kim.
  const [interest, setInterest] = useState(fixed ? String(Math.min(monthlyInterest, fixed)) : '');
  const [principal, setPrincipal] = useState(fixed ? String(Math.max(0, fixed - monthlyInterest)) : '');
  const [from, setFrom] = useState(''); const [date, setDate] = useState(today()); const [note, setNote] = useState('');
  const [err, setErr] = useState<Record<string, string>>({});
  const { busy, error, exec } = useAction(() => { close(); onSaved(); });
  const i = parseMoneyOrNull(interest) ?? 0;
  const p = parseMoneyOrNull(principal) ?? 0;
  const t = i + p;
  const src = wallets.find(w => w.id === from);

  const submit = () => {
    const e: Record<string, string> = {};
    if (interest.trim() && parseMoneyOrNull(interest) === null) e.interest = 'Lãi không hợp lệ';
    if (principal.trim() && parseMoneyOrNull(principal) === null) e.principal = 'Gốc không hợp lệ';
    if (!e.interest && !e.principal) {
      if (t <= 0) e.principal = 'Nhập tiền lãi hoặc tiền gốc lớn hơn 0';
      else if (splitDebtPayment(remaining, 0, t, i).excess > 0) e.principal = `Gốc trả (${formatVND(p)}) vượt dư nợ còn lại ${formatVND(remaining)}`;
    }
    if (!from) e.from = 'Chọn ví trả (để trừ số dư)';
    else if (src && t > (Number(src.balance) || 0)) e.from = `Số dư ${src.name} chỉ còn ${formatVND(Number(src.balance) || 0)}`;
    setErr(e);
    if (Object.keys(e).length) return;
    exec(() => addPayment(uid(), id, { totalAmount: t, principal: p, interest: i, paymentDate: date, note, sourceId: from }), 'Đã ghi khoản trả nợ');
  };

  return (
    <FormSheet open onClose={close} title={`Trả nợ — ${debt?.lender ?? ''}`} onSubmit={submit} submitLabel="Ghi khoản trả" busy={busy} error={error}
      description={`Còn nợ ${formatVND(remaining)}. Lãi tự ghi thành chi phí tài chính; gốc làm giảm dư nợ.`}>
      <MoneyField label="Tiền lãi" value={interest} onChange={setInterest} allowZero error={err.interest}
        hint={`Gợi ý lãi 1 tháng theo lãi suất đã nhập: ${formatVND(monthlyInterest)}`} />
      <MoneyField label="Tiền gốc" value={principal} onChange={setPrincipal} allowZero error={err.principal}
        hint={`Tổng phải trả: ${formatVND(t)} · dư nợ sau khi trả: ${formatVND(Math.max(0, remaining - p))}`} />
      <WalletSelect label="Trả từ ví" value={from} onChange={setFrom} wallets={wallets} error={err.from} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Ngày"><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="num" /></Field>
        <Field label="Ghi chú"><Input value={note} onChange={e => setNote(e.target.value)} placeholder="Tuỳ chọn" /></Field>
      </div>
    </FormSheet>
  );
}

// ───────── Sửa thông tin khoản vay (nhập nhầm) ─────────
const isoOf = (d: Date | null) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '');
export function EditLoanSheet({ id, onSaved }: { id: string; onSaved: () => void }) {
  const close = useAssetsUI(s => s.close);
  const debt = useData(s => s.data.debts).find(d => d.id === id);
  const cards = useData(s => s.data.sources).filter(s => s.type === 'credit' && s.status !== 'inactive');
  const [lender, setLender] = useState(String(debt?.lender ?? ''));
  const [principal, setPrincipal] = useState(String(debt?.principal ?? ''));
  const [remaining, setRemaining] = useState(String(debt?.remainingBalance ?? ''));
  const [rate, setRate] = useState(hasInterestRate(debt?.interestRate) ? String(debt?.interestRate) : '');
  const [term, setTerm] = useState(Number(debt?.term) > 0 ? String(debt?.term) : '');
  const [method, setMethod] = useState<'annuity' | 'flexible'>(debt?.repaymentMethod === 'annuity' ? 'annuity' : 'flexible');
  const [monthly, setMonthly] = useState(Number(debt?.monthlyPayment) > 0 ? String(debt?.monthlyPayment) : '');
  const [due, setDue] = useState(isoOf(toDate(debt?.dueDate)));
  const [card, setCard] = useState<string>(String(debt?.creditCardId || 'none'));
  const [note, setNote] = useState(String(debt?.note ?? ''));
  const [err, setErr] = useState<Record<string, string>>({});
  const { busy, error, exec } = useAction(() => { close(); onSaved(); });
  const p = parseMoneyOrNull(principal), r = parseMoneyOrNull(remaining);
  const oldRemaining = Number(debt?.remainingBalance) || 0;
  const changed = r !== null && r !== oldRemaining;
  const paidSoFar = p !== null && r !== null ? Math.max(0, p - r) : null;
  const oldRate = hasInterestRate(debt?.interestRate) ? Number(debt?.interestRate) : null;

  const submit = () => {
    const e: Record<string, string> = {};
    if (!rate.trim()) e.rate = 'Nhập lãi suất; vay không lãi thì nhập 0';
    const bad = validateLoanCorrection({ lender, principal: p ?? NaN, remainingBalance: r ?? NaN, interestRate: rate.trim() ? Number(rate) : undefined, term: term.trim() ? Number(term) : 0, monthlyPayment: monthly.trim() ? parseMoneyOrNull(monthly) : null });
    if (bad) e.form = bad;
    setErr(e);
    if (Object.keys(e).length) return;
    exec(() => correctDebt(uid(), id, {
      lender, principal: p, remainingBalance: r, interestRate: Number(rate), term: Number(term) || 0, repaymentMethod: method,
      monthlyPayment: monthly.trim() ? parseMoneyOrNull(monthly) : null, dueDate: due || null, note, creditCardId: card === 'none' ? null : card
    }), 'Đã sửa khoản vay');
  };
  const section = (t: string) => <p className="pt-1 text-sm font-medium text-muted-foreground">{t}</p>;
  return (
    <FormSheet open onClose={close} title={`Sửa thông tin — ${debt?.lender ?? ''}`} onSubmit={submit} submitLabel="Lưu thay đổi" busy={busy} error={error ?? err.form ?? null}
      description="Dùng khi nhập nhầm. Các lần đã trả giữ nguyên. Dư nợ theo ngân hàng thì ghi trả nợ hoặc đối soát, lãi đổi thật từ một ngày thì dùng Cập nhật lãi suất.">
      {section('Khoản vay')}
      <Field label="Bên cho vay"><Input value={lender} onChange={e => setLender(e.target.value)} /></Field>
      <MoneyField label="Tiền vay gốc" value={principal} onChange={setPrincipal} />
      <MoneyField label="Dư nợ còn lại" value={remaining} onChange={setRemaining} allowZero hint={paidSoFar !== null ? `Đã trả gốc ${formatVND(paidSoFar)}` : undefined} />
      {section('Lãi suất và cách trả')}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Lãi suất (%/năm)" error={err.rate} hint="Nhập 0 nếu vay không lãi"><Input value={rate} onChange={e => setRate(e.target.value)} inputMode="decimal" className="num" placeholder="vd: 9.5" aria-invalid={!!err.rate} /></Field>
        <Field label="Kỳ hạn (tháng)" hint="Để trống nếu không có"><Input value={term} onChange={e => setTerm(e.target.value)} inputMode="numeric" className="num" placeholder="vd: 36" /></Field>
      </div>
      <Field label="Cách trả">
        <Select value={method} onValueChange={v => setMethod(v as any)}>
          <SelectTrigger aria-label="Cách trả"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="flexible">Linh hoạt (trả khi có tiền)</SelectItem>
            <SelectItem value="annuity">Trả góp đều hằng tháng (gốc + lãi)</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <MoneyField label="Trả mỗi tháng (tuỳ chọn)" value={monthly} onChange={setMonthly} allowZero />
      {section('Thời hạn và liên kết')}
      <Field label="Ngày đáo hạn (tuỳ chọn)"><Input type="date" value={due} onChange={e => setDue(e.target.value)} className="num" /></Field>
      {cards.length > 0 && (
        <Field label="Vay hoặc trả góp trên thẻ tín dụng" hint="Chọn thẻ nếu khoản này đã nằm trong dư nợ của thẻ đó: tổng nợ chỉ tính một lần, và trả gốc khoản vay sẽ giảm dư nợ thẻ.">
          <Select value={card} onValueChange={setCard}>
            <SelectTrigger aria-label="Thẻ tín dụng của khoản vay"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Không, đây là khoản vay riêng</SelectItem>{cards.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      <Field label="Ghi chú"><Input value={note} onChange={e => setNote(e.target.value)} placeholder="Tuỳ chọn" /></Field>
      {changed && <p role="note" className="num rounded-md bg-warn-soft px-3 py-2 text-sm text-warn">Dư nợ đổi từ {formatVND(oldRemaining)} thành {formatVND(r!)}, làm tài sản ròng {r! > oldRemaining ? 'giảm' : 'tăng'} {formatVND(Math.abs(r! - oldRemaining))}. Việc này được ghi vào lịch sử.</p>}
      {rate.trim() && Number.isFinite(Number(rate)) && Number(rate) !== oldRate && <p role="note" className="rounded-md bg-muted px-3 py-2 text-sm">Lãi suất đổi {oldRate === null ? 'từ chưa nhập' : `từ ${oldRate}%`} thành <b>{Number(rate)}%/năm</b>{Number(rate) === 0 ? ' (vay không lãi)' : ''}. Muốn giữ lịch sử lãi theo ngày, dùng Cập nhật lãi suất.</p>}
    </FormSheet>
  );
}

// ───────── Cập nhật lãi suất ─────────
export function LoanRateSheet({ id, payments, onSaved }: { id: string; payments: any[]; onSaved: () => void }) {
  const close = useAssetsUI(s => s.close);
  const { debts, recurringTemplates } = useData(s => s.data);
  const debt = debts.find(d => d.id === id);
  const remaining = Number(debt?.remainingBalance) || 0;
  const oldRate = Number(debt?.interestRate) || 0;
  const paidCount = payments.filter(p => p.debtId === id).length;
  const [rate, setRate] = useState(String(oldRate));
  const [effective, setEffective] = useState(today());
  const [left, setLeft] = useState(Number(debt?.term) > 0 ? String(Math.max(1, Number(debt?.term) - paidCount)) : '');
  const [monthly, setMonthly] = useState(debt?.monthlyPayment ? String(debt.monthlyPayment) : '');
  const [updateTpl, setUpdateTpl] = useState(true);
  const [err, setErr] = useState<Record<string, string>>({});
  const { busy, error, exec } = useAction(() => { close(); onSaved(); });

  const newRate = Number(rate);
  const validRate = rate.trim() !== '' && Number.isFinite(newRate) && newRate >= 0 && newRate <= 100;
  const monthsLeft = Math.round(Number(left)) || 0;
  const suggested = validRate && remaining > 0 && monthsLeft > 0 ? Math.round(fixedMonthlyPayment(remaining, newRate, monthsLeft)) : 0;
  const newMonthly = parseMoneyOrNull(monthly);
  const monthlyChanged = newMonthly !== null && newMonthly > 0 && newMonthly !== (Number(debt?.monthlyPayment) || 0);
  const tpls = recurringTemplates.filter((t: any) => t.type === 'payment' && t.paymentType === 'debt' && t.debtId === id);
  const interestOf = (r: number) => Math.round(remaining * r / 12 / 100);

  const submit = () => {
    const e: Record<string, string> = {};
    if (!validRate) e.rate = 'Lãi suất từ 0 đến 100 %/năm';
    if (monthly.trim() && (newMonthly === null || newMonthly <= 0)) e.monthly = 'Số trả mỗi tháng không hợp lệ';
    setErr(e);
    if (Object.keys(e).length) return;
    exec(() => updateLoanTerms(uid(), id, {
      interestRate: newRate, effectiveDate: effective, monthlyPayment: monthlyChanged ? newMonthly : null,
      templateIds: monthlyChanged && updateTpl ? tpls.map((t: any) => t.id) : []
    }), 'Đã cập nhật lãi suất');
  };

  return (
    <FormSheet open onClose={close} title={`Cập nhật lãi suất — ${debt?.lender ?? ''}`} onSubmit={submit} submitLabel="Lưu lãi suất" busy={busy} error={error}
      description="Lãi từ nay tính theo lãi suất mới. Các lần đã trả giữ nguyên, không bị tính lại.">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Lãi suất mới (%/năm)" error={err.rate} hint={`Hiện tại ${oldRate}%/năm`}><Input value={rate} onChange={e => setRate(e.target.value)} inputMode="decimal" className="num" aria-invalid={!!err.rate} /></Field>
        <Field label="Áp dụng từ ngày"><Input type="date" value={effective} onChange={e => setEffective(e.target.value)} className="num" /></Field>
      </div>
      {validRate && newRate !== oldRate && remaining > 0 && (
        <p className="num rounded-md bg-muted px-3 py-2 text-sm">Lãi một tháng ước tính theo dư nợ {formatVND(remaining)}: <b>{formatVND(interestOf(oldRate))}</b> → <b>{formatVND(interestOf(newRate))}</b> ({interestOf(newRate) - interestOf(oldRate) > 0 ? '+' : '−'}{formatVND(Math.abs(interestOf(newRate) - interestOf(oldRate)))}).</p>
      )}
      <Field label="Số kỳ còn lại (tuỳ chọn)" hint="Dùng để gợi ý số trả hằng tháng nếu bên cho vay tính lại theo niên kim."><Input value={left} onChange={e => setLeft(e.target.value)} inputMode="numeric" className="num" /></Field>
      <MoneyField label="Số trả mỗi tháng" value={monthly} onChange={setMonthly} allowZero error={err.monthly}
        hint={suggested > 0 ? <>Gợi ý theo lãi mới và {monthsLeft} kỳ còn lại: <button type="button" className="font-medium text-primary underline" onClick={() => setMonthly(String(suggested))}>{formatVND(suggested)}</button>. Cứ theo thông báo của ngân hàng nếu khác.</> : 'Giữ nguyên nếu ngân hàng không đổi số trả.'} />
      {monthlyChanged && tpls.length > 0 && (
        <label className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={updateTpl} onChange={e => setUpdateTpl(e.target.checked)} />
          <span>Đổi luôn số tiền ở {tpls.length} mẫu trả nợ định kỳ của khoản vay này. Các khoản dự kiến đã sinh sẵn giữ số cũ — sửa số khi xác nhận.</span>
        </label>
      )}
    </FormSheet>
  );
}

// ───────── Lịch trả nợ + lịch sử ─────────
export function ScheduleSheet({ id, payments }: { id: string; payments: any[] }) {
  const close = useAssetsUI(s => s.close);
  const debt = useData(s => s.data.debts).find(d => d.id === id);
  const remaining = Number(debt?.remainingBalance) || 0;
  const rate = Number(debt?.interestRate) || 0;
  const monthly = Number(debt?.monthlyPayment) || (Number(debt?.term) > 0 ? Math.round(fixedMonthlyPayment(remaining, rate, Number(debt.term))) : 0);
  const rows = useMemo(() => (remaining > 0 && monthly > 0 ? amortizationSchedule(remaining, rate, monthly) : []), [remaining, rate, monthly]);
  const totalInterest = rows.reduce((s, r) => s + r.interestPaid, 0);
  const history = payments.filter(p => p.debtId === id).sort((a, b) => (toDate(b.paymentDate)?.getTime() ?? 0) - (toDate(a.paymentDate)?.getTime() ?? 0));
  const isDesktop = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 768px)').matches;

  return (
    <Sheet open onOpenChange={(o) => !o && close()}>
      <SheetContent side={isDesktop ? 'right' : 'bottom'} className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{debt?.lender} — lịch trả nợ</SheetTitle>
          <SheetDescription>Còn nợ {formatVND(remaining)} · lãi {rate}%/năm</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-5">
          {rows.length > 0 ? (
            <section>
              <p className="num mb-2 text-sm">Trả <b>{formatVND(monthly)}</b>/tháng → hết nợ sau <b>{rows.length} tháng</b>, tổng lãi còn phải trả <b>{formatCompact(totalInterest)}</b>.</p>
              <div className="max-h-64 overflow-y-auto rounded-md border border-border">
                <table className="num w-full text-xs">
                  <thead className="sticky top-0 bg-muted text-muted-foreground"><tr><th className="p-2 text-left">Kỳ</th><th className="p-2 text-right">Gốc</th><th className="p-2 text-right">Lãi</th><th className="p-2 text-right">Còn nợ</th></tr></thead>
                  <tbody>{rows.slice(0, 60).map(r => (
                    <tr key={r.month} className="border-t border-border"><td className="p-2">{r.month}</td><td className="p-2 text-right">{formatCompact(r.principalPaid)}</td><td className="p-2 text-right">{formatCompact(r.interestPaid)}</td><td className="p-2 text-right">{formatCompact(r.remainingBalance)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
              {rows.length > 60 && <p className="mt-1 text-xs text-muted-foreground">Hiện 60 kỳ đầu / {rows.length} kỳ.</p>}
            </section>
          ) : (
            <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              {remaining <= 0 ? 'Khoản vay đã trả hết.' : monthly <= 0 ? 'Chưa đặt khoản trả hằng tháng nên chưa lập được lịch. Thêm số tiền trả mỗi tháng cho khoản vay để xem lịch.' : 'Khoản trả hằng tháng thấp hơn lãi phát sinh — dư nợ sẽ không bao giờ giảm. Tăng số trả mỗi tháng.'}
            </p>
          )}
          <section>
            <h3 className="mb-2 text-sm font-medium">Đã trả ({history.length})</h3>
            {history.length === 0 ? <p className="text-sm text-muted-foreground">Chưa có khoản trả nào.</p> : (
              <ul className="divide-y divide-border rounded-md border border-border text-sm">
                {history.map(p => (
                  <li key={p.id} className="flex items-baseline justify-between gap-2 px-3 py-2">
                    <span className="num text-muted-foreground">{dmy(toDate(p.paymentDate))}</span>
                    <span className="num text-xs text-muted-foreground">gốc {formatCompact(Number(p.principal) || 0)} · lãi {formatCompact(Number(p.interest) || 0)}</span>
                    <span className="num font-medium">{formatVND(Number(p.totalAmount ?? p.amount) || 0)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
